import * as React from 'react'

// TODO: After state/overlaytrigger gets removed,
// move TriggerVariant definition here
import { triggerListeners } from '../utils/triggerListeners'
import { Omit } from '../utils/types'
import {
  OverlayTriggerVariant,
  OverlayTriggerVariantNS,
} from '../state/OverlayTrigger'
import { useHotkey } from './Hotkey'

/* TYPES */

export interface TriggerConfig {
  disable: React.EventHandler<any>
  disableKeys?: Array<string>
  enable: React.EventHandler<any>
  enableKeys?: Array<string>
  isEnabled?: boolean
  scope: React.RefObject<HTMLElement>
  trigger?: OverlayTriggerVariant
}

export interface TriggerProps extends TriggerConfig {
  children: React.ReactNode
}

interface TriggerRenderProps extends Omit<TriggerConfig, 'scope'> {
  children: React.ReactNode | ((props: TriggerApi) => React.ReactElement)
  scope?: React.RefObject<HTMLElement>
}

export interface TriggerApi {
  disable: React.EventHandler<any>
  enable: React.EventHandler<any>
  isVisible: boolean
  toggle: React.EventHandler<any>
}

/* HOOK */

export const useTrigger: (config: TriggerConfig) => TriggerApi = ({
  disable,
  disableKeys = [],
  enable,
  enableKeys = [],
  isEnabled = false,
  scope,
  trigger = OverlayTriggerVariantNS.click,
}) => {
  triggerListeners({
    off: disable,
    on: enable,
    scope,
    trigger,
    value: isEnabled,
  })

  useHotkey({
    key: enableKeys,
    handler: enable,
    preventDefault: true,
    scope,
  })

  useHotkey({
    key: disableKeys,
    handler: disable,
    preventDefault: true,
    scope,
  })

  return {
    disable,
    enable,
    isVisible: isEnabled,
    toggle: isEnabled ? disable : enable,
  }
}

/* DECORATOR */

export const withTrigger = <T extends {}>(config: TriggerConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { Trigger: TriggerConfig }) => {
  return (
    <Component
      {...props}
      trigger={useTrigger({ ...config, ...props.Trigger })}
    />
  )
}

/* RENDER PROP */

export const Trigger = React.forwardRef<HTMLElement, TriggerRenderProps>(
  ({ children, ...props }, forwardedRef) => {
    const scope = props.scope || (forwardedRef as React.RefObject<HTMLElement>)

    const trigger = useTrigger({ ...props, scope })

    const element =
      typeof children === 'function'
        ? (children as ((props: TriggerApi) => React.ReactElement))(trigger)
        : (children as React.ReactElement)

    return (
      element &&
      React.cloneElement(element, {
        ...element.props,
        ref: scope,
        open: trigger.isVisible,
      })
    )
  }
)

/* CONTEXT */

const noop: (method: string) => React.EventHandler<any> = method => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in an \`<TriggerProvider>\``
  )

export const initTrigger: TriggerApi = {
  disable: noop('disable'),
  enable: noop('enable'),
  isVisible: false,
  toggle: noop('toggle'),
}

export const TriggerContext = React.createContext(initTrigger)

export const TriggerProvider = ({ children, ...props }: TriggerProps) => (
  <TriggerContext.Provider value={useTrigger(props)}>
    {children}
  </TriggerContext.Provider>
)

export const consumeTrigger = () => React.useContext(TriggerContext)
